import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { propertyService } from '../../../services/propertyService';
import { sendPropertyInquiryEmail } from '../../../services/resendEmailService';
import { trackInquirySubmitted } from '../../../services/userJourneyService';
import { gaTrackInquiryCreated } from '../../../services/gaAnalyticsService';

const InquiryForm = ({ property }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    message: '',
    clientPhone: '',
    preferredContactTime: 'Journée'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const contactTimeOptions = [
    { value: 'Matin', label: 'Matin (8h - 12h)' },
    { value: 'Journée', label: 'Journée (12h - 17h)' },
    { value: 'Soir', label: 'Soir (17h - 20h)' },
    { value: 'Weekend', label: 'Week-end' },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.message?.trim() || !formData?.clientPhone?.trim()) {
      setError('Veuillez remplir tous les champs requis');
      return;
    }
    try {
      setIsSubmitting(true);
      setError(null);
      if (!property?.id) throw new Error('Property ID is required');
      const inquiryData = {
        property_id: property?.id,
        client_id: null,
        message: formData?.message?.trim(),
        client_phone: formData?.clientPhone?.trim(),
        preferred_contact_time: formData?.preferredContactTime,
        status: 'new'
      };
      const result = await propertyService?.submitPropertyInquiry(inquiryData);
      if (result) {
        // Track inquiry submission
        trackInquirySubmitted(property, { preferredContactTime: formData?.preferredContactTime });
        gaTrackInquiryCreated(property, { preferredContactTime: formData?.preferredContactTime });

        const recipientEmail = user?.email;
        if (recipientEmail) {
          sendPropertyInquiryEmail({
            user_id: user?.id,
            client_name: user?.user_metadata?.full_name || user?.email,
            client_email: recipientEmail,
            client_phone: formData?.clientPhone?.trim(),
            property_title: property?.title,
            property_type: property?.property_type,
            message: formData?.message?.trim(),
            preferred_contact_time: formData?.preferredContactTime,
          });
        }
        setSubmitted(true);
        setFormData({ message: '', clientPhone: '', preferredContactTime: 'Journée' });
      }
    } catch (err) {
      console.error('Error submitting inquiry:', err);
      if (err?.code === '42501' || err?.message?.includes('row-level security')) {
        setError('Erreur d\'autorisation lors de l\'envoi de la demande. Veuillez réessayer.');
      } else if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError') || err?.message?.includes('fetch')) {
        setError('Impossible de se connecter au service. Votre projet Supabase pourrait être en pause.');
      } else {
        setError('Une erreur est survenue lors de l\'envoi de votre demande. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF', minimumFractionDigits: 0, maximumFractionDigits: 0 })?.format(price);
  };

  if (!property) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="text-center py-4">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Check" size={28} color="#16a34a" />
          </div>
          <h3 className="font-heading font-semibold text-base text-foreground mb-2">
            Demande envoyée !
          </h3>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            Votre demande a été transmise à notre agent. Vous serez contacté dans les plus brefs délais.
          </p>
          <button
            onClick={() => setSubmitted(false)}
            className="text-sm font-medium text-brand-gold hover:text-brand-earth transition-colors"
          >
            Envoyer une autre demande
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-5 sticky top-24">
      {/* Property Summary Card */}
      <div className="mb-5 p-4 bg-muted/40 rounded-md border border-border">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-brand-gold/10 rounded-md flex items-center justify-center flex-shrink-0">
            <Icon name="Home" size={18} color="var(--color-brand-gold)" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground line-clamp-2 mb-0.5">
              {property?.title}
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              {property?.communes?.name} · {property?.property_type}
            </p>
            <p className="text-lg font-bold text-brand-earth">
              {formatPrice(property?.price)}
            </p>
          </div>
        </div>
      </div>
      {/* Form Header */}
      <div className="flex items-center gap-2 mb-4">
        <Icon name="MessageCircle" size={18} color="var(--color-brand-gold)" />
        <h3 className="font-heading font-semibold text-base text-foreground">
          Demander des informations
        </h3>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-xs flex items-start gap-2">
            <Icon name="AlertCircle" size={14} className="flex-shrink-0 mt-0.5" color="currentColor" />
            <span>{error}</span>
          </div>
        )}

        {/* Message */}
        <div className="space-y-1.5">
          <label htmlFor="message" className="text-sm font-medium text-foreground">
            Message <span className="text-destructive">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData?.message}
            onChange={handleInputChange}
            placeholder="Décrivez votre projet ou posez vos questions..."
            rows={3}
            className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md resize-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors"
            required
          />
        </div>

        {/* Phone */}
        <div className="space-y-1.5">
          <label htmlFor="clientPhone" className="text-sm font-medium text-foreground">
            Téléphone <span className="text-destructive">*</span>
          </label>
          <Input
            id="clientPhone"
            name="clientPhone"
            type="tel"
            value={formData?.clientPhone}
            onChange={handleInputChange}
            placeholder="+225 XX XX XX XX XX"
            required
          />
        </div>

        {/* Contact Time */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">
            Moment de contact préféré
          </label>
          <Select
            options={contactTimeOptions}
            value={formData?.preferredContactTime}
            onChange={(val) => setFormData(prev => ({ ...prev, preferredContactTime: val }))}
            placeholder="Choisir un moment"
          />
        </div>

        <Button
          type="submit"
          variant="elegant"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          iconName={isSubmitting ? 'Loader' : 'Send'}
          iconPosition="left"
        >
          {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
        </Button>
      </form>
      {/* Agent Contact */}
      {property?.agents && (
        <div className="mt-5 pt-4 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Votre agent</p>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-brand-gold/10 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="User" size={15} color="var(--color-brand-gold)" />
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{property?.agents?.full_name}</p>
              {property?.agents?.email && (
                <p className="text-xs text-muted-foreground">{property?.agents?.email}</p>
              )}
            </div>
          </div>
          {property?.agents?.phone && (
            <Button
              asChild
              variant="outline"
              size="sm"
              fullWidth
              iconName="Phone"
              iconPosition="left"
            >
              <a href={`tel:${property?.agents?.phone}`}>Appeler maintenant</a>
            </Button>
          )}
        </div>
      )}
      {/* Trust Indicators */}
      <div className="mt-4 pt-4 border-t border-border grid grid-cols-3 gap-2 text-center">
        {[
          { icon: 'Shield', label: 'Sécurisé' },
          { icon: 'Clock', label: 'Réponse rapide' },
          { icon: 'Star', label: 'Service expert' },
        ]?.map(({ icon, label }) => (
          <div key={label} className="py-1.5">
            <Icon name={icon} size={15} color="var(--color-success)" className="mx-auto mb-1" />
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default InquiryForm;